import type { AuthAdapter } from "@repo/app-ui";
import type { CognitoIdentityProvider, CognitoTokens } from "@repo/auth-core";
import {
  exchangeCodeForTokens,
  logoutUrl,
  refreshTokens,
  startLogin,
} from "./cognito";
import { clearVerifier, peekVerifier } from "./pkce";
import { clearTokens, loadTokens, saveTokens } from "./storage";

/**
 * Web implementation of the shared {@link AuthAdapter}. Sign-in redirects the
 * top-level frame to the Cognito Hosted UI; the PKCE exchange completes on the
 * next page load from the `/login` callback (so `login()` never resolves — the
 * page unloads first — and `bootstrap()` does the redemption). Tokens live in
 * localStorage.
 */
export const webAuthAdapter: AuthAdapter = {
  async bootstrap(): Promise<CognitoTokens | null> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const stateParam = url.searchParams.get("state");

    if (window.location.pathname === "/login" && code) {
      const pending = peekVerifier();
      // Validate before consuming. A reload of the callback URL or a parallel
      // tab that already completed login leaves stale params here; bailing
      // without clearing the verifier lets the user retry.
      if (
        !pending ||
        !stateParam ||
        stateParam !== pending.state ||
        !pending.verifier
      ) {
        throw new Error("Authentication state mismatch.");
      }
      const tokens = await exchangeCodeForTokens(code, pending.verifier);
      // Code redeemed successfully — only now is it safe to drop the PKCE
      // state and strip the code from the URL.
      clearVerifier();
      window.history.replaceState({}, "", pending.returnTo || "/");
      return tokens;
    }

    return loadTokens();
  },

  async login(
    returnTo: string,
    identityProvider?: CognitoIdentityProvider,
  ): Promise<CognitoTokens | null> {
    // Navigates away; the callback completes on the next load via bootstrap().
    await startLogin(returnTo, identityProvider);
    return null;
  },

  refresh(refreshToken: string): Promise<CognitoTokens> {
    return refreshTokens(refreshToken);
  },

  persist(tokens: CognitoTokens): void {
    saveTokens(tokens);
  },

  clear(): void {
    clearTokens();
  },

  logout(): void {
    window.location.assign(logoutUrl());
  },
};
